'use client';

import { FC, useEffect, useState, useContext, useCallback } from 'react';
import {
  Dialog,
  Flex,
  Button,
  ButtonGroup,
  Text,
  Pill,
  Alert,
} from '@/_components';
import { TableLoading } from '@/_components/TableLoading';
import {
  getDropLinkUploads,
  deleteDropLinkUpload,
  toggleUploadAsSeen,
  downloadUpload,
} from '@/_api/drop-link';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { DropLink, DropLinkUpload } from '@/_types/models/dropLink';
import { DropLinksContext } from '@/_contexts/DropLinksProvider';
import invariant from 'tiny-invariant';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';

interface DropLinkUploadsDialogProps {
  dropLink: DropLink | null;
  isOpen: boolean;
  onClose: () => void;
}

// Define the table headers as an array for maintainability
const DROP_LINK_UPLOADS_TABLE_HEADERS = [
  { id: 'description', label: 'Description', width: 180 },
  { id: 'uploaded_by', label: 'Uploaded By', width: 180 },
  { id: 'uploaded_at', label: 'Uploaded At', width: 130 },
  { id: 'actions', label: 'Actions', width: 170 },
];

export const DropLinkUploadsDialog: FC<DropLinkUploadsDialogProps> = ({
  dropLink,
  isOpen,
  onClose,
}) => {
  const [uploads, setUploads] = useState<DropLinkUpload[]>([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const { addToast } = useContext(ToastContext);
  const { token, user } = useContext(UserContext);
  const { onToggleAddClient, setDropLinkUpload } = useContext(CompanyContext);
  const { closeAllDropLinkDialogs, loadDropLinks } =
    useContext(DropLinksContext);

  const fetchUploads = useCallback(async () => {
    if (!token) {
      addToast('error', 'You must be logged in to fetch uploads');
      return;
    }
    invariant(dropLink, 'Drop link is required to fetch uploads');

    setLoadingUploads(true);
    try {
      const uploads = await getDropLinkUploads({
        dropLinkId: dropLink.id,
        token: token as string,
      });
      setUploads(uploads as DropLinkUpload[]);
    } catch (e) {
      console.error(e);
      addToast('error', 'Failed to fetch uploads', { error: e });
    } finally {
      setLoadingUploads(false);
    }
  }, [dropLink, token, addToast]);

  const openCreateEntityAndUserDialog = (upload: DropLinkUpload) => {
    setDropLinkUpload(upload);
    onToggleAddClient();
    closeAllDropLinkDialogs();
  };

  useEffect(() => {
    if (isOpen && dropLink) {
      fetchUploads();
    } else {
      setUploads([]);
      setDropLinkUpload(null);
    }
  }, [isOpen, fetchUploads, setDropLinkUpload, dropLink]);

  const handleDelete = async (upload: DropLinkUpload) => {
    if (!window.confirm('Are you sure you want to delete this upload?')) {
      return;
    }
    if (!token) {
      addToast('error', 'You must be logged in to delete uploads');
      return;
    }
    invariant(dropLink, 'Drop link is required to delete upload');
    invariant(upload?.id, 'Upload ID is required to delete upload');

    setDeletingId(upload.id);
    try {
      const response = await deleteDropLinkUpload({
        dropLinkId: dropLink.id,
        uploadId: upload.id,
        token: token as string,
      });
      if (response.error) {
        addToast('error', response.error);
        return;
      }
      addToast('success', 'Upload deleted');
      fetchUploads();
      loadDropLinks();
    } catch (e) {
      addToast('error', 'Failed to delete upload');
    } finally {
      setDeletingId(null);
    }
  };

  const download = async (upload: DropLinkUpload) => {
    if (!token) {
      addToast('error', 'You must be logged in to download upload');
      return;
    }
    invariant(dropLink, 'Drop link is required to download upload');
    invariant(upload?.id, 'Upload ID is required to download upload');

    try {
      await downloadUpload({
        dropLinkId: dropLink.id,
        uploadId: upload.id,
        token: token as string,
      });
    } catch (e) {
      addToast('error', 'Failed to download upload', { error: e });
    } finally {
      fetchUploads();
      loadDropLinks();
    }
  };

  const toggleAsSeen = async (uploadId: number) => {
    if (!token) {
      addToast('error', 'You must be logged in to toggle upload seen');
      return;
    }
    invariant(dropLink, 'Drop link is required to toggle upload seen status');
    invariant(uploadId, 'Upload ID is required to toggle upload seen status');

    await toggleUploadAsSeen({
      dropLinkId: dropLink.id,
      uploadId,
      token: token as string,
    });
    addToast('success', 'Upload marked as seen');
    fetchUploads();
    loadDropLinks();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Drop Link Uploads"
      isWide={true}
    >
      <Dialog.Content>
        <Flex isColumn gap={10}>
          <Flex isYCentered gap={8}>
            <ButtonGroup align="left">
              <Button
                intent="secondary"
                iconLeft="caret-left"
                isIconOnly={true}
                onClick={onClose}
                size="tiny"
                value="Back"
                withTooltip={true}
              />
              <Button
                intent="secondary"
                iconRight="refresh"
                isIconOnly={true}
                onClick={fetchUploads}
                size="tiny"
                value="Refresh"
                withTooltip={true}
              />
            </ButtonGroup>
            <Text type="small" fontWeight="semi-bold">
              Uploads for: {dropLink?.name}
            </Text>
          </Flex>
          <Alert type="info" icon="info">
            Click <b>Download</b> to download a zipped file of the uploaded
            documents, and click <b>Mark Seen</b> to mark the upload as seen.
            Only you can see the upload if it is marked as seen.
          </Alert>
          {loadingUploads ? (
            <TableLoading />
          ) : (
            <table
              className="sorabanTable"
              style={{ width: '100%', tableLayout: 'auto' }}
            >
              <thead>
                <tr>
                  {DROP_LINK_UPLOADS_TABLE_HEADERS.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        padding: '12px 24px',
                        minWidth: header.width,
                        fontSize: '14px',
                        width: header.width,
                      }}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {uploads.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{ textAlign: 'center', padding: 24 }}
                    >
                      No uploads found.
                    </td>
                  </tr>
                ) : (
                  uploads.map((upload: DropLinkUpload) => {
                    const isANewUpload = upload.unseen_user_ids.includes(
                      user?.id ?? 0,
                    );
                    return (
                      <tr key={upload.id}>
                        <td
                          style={{
                            padding: '12px 24px',
                            fontSize: '14px',
                            width: DROP_LINK_UPLOADS_TABLE_HEADERS[0].width,
                          }}
                        >
                          <Flex gap={4} isYCentered>
                            <Text type="small">{upload.description}</Text>
                            {isANewUpload && (
                              <Pill intent="danger" label="New" />
                            )}
                          </Flex>
                        </td>
                        <td
                          style={{
                            padding: '12px 24px',
                            fontSize: '14px',
                            width: DROP_LINK_UPLOADS_TABLE_HEADERS[1].width,
                          }}
                        >
                          <Flex isColumn>
                            <Text type="small">
                              {upload.first_name} {upload.last_name}
                            </Text>
                            <Text type="small" color="secondary">
                              {upload.email}
                            </Text>
                            <Text type="small" color="secondary">
                              {upload.phone_number}
                            </Text>
                          </Flex>
                        </td>
                        <td
                          style={{
                            padding: '12px 24px',
                            fontSize: '14px',
                            width: DROP_LINK_UPLOADS_TABLE_HEADERS[2].width,
                          }}
                        >
                          <Text type="small">
                            {new Date(upload.uploaded_at).toLocaleDateString()}
                          </Text>
                        </td>
                        <td
                          style={{
                            padding: '12px 24px',
                            fontSize: '14px',
                            width: DROP_LINK_UPLOADS_TABLE_HEADERS[3].width,
                          }}
                        >
                          <ButtonGroup isSmallGroup={true}>
                            <Button
                              intent="secondary"
                              iconLeft="download"
                              isIconOnly={true}
                              size="tiny"
                              value="Download"
                              withTooltip={true}
                              onClick={() => download(upload)}
                            />
                            <Button
                              intent="secondary"
                              iconLeft={isANewUpload ? 'eye' : 'eye-closed'}
                              isIconOnly={true}
                              size="tiny"
                              value={isANewUpload ? 'Mark Seen' : 'Mark Unseen'}
                              withTooltip={true}
                              onClick={() => toggleAsSeen(upload.id)}
                            />
                            <Button
                              intent="secondary"
                              iconLeft="plus"
                              isIconOnly={true}
                              size="tiny"
                              value="Create Entity"
                              withTooltip={true}
                              onClick={() => {
                                openCreateEntityAndUserDialog(upload);
                              }}
                            />
                            <Button
                              intent="danger"
                              iconLeft="trash"
                              isIconOnly={true}
                              size="tiny"
                              value="Delete"
                              withTooltip={true}
                              isDisabled={deletingId === upload.id}
                              onClick={() => handleDelete(upload)}
                            />
                          </ButtonGroup>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
